/** Entry gate v2 — single source for calcGate (scanner + runAnalysis). */

const COLORS = {
  red: "#ef4444",
  green: "#22c55e",
  cyan: "#06b6d4",
  yellow: "#eab308",
  orange: "#f97316",
  textDim: "#64748b",
};

const CRIT_META = {
  death_cross: { shortLabel: "Death cross" },
  below_stop: { shortLabel: "Below stop" },
  bearish_score: { shortLabel: "Score too bearish" },
  post_drop: { shortLabel: "Post-drop cooldown" },
  extended_distribution: { shortLabel: "Extended + money leaving" },
  fakeout: { shortLabel: "Weak breakout volume" },
};

const ADV_META = {
  extended_bb: { shortLabel: "Above BB upper" },
  vol_div: { shortLabel: "Weak volume rally" },
  cmf_dist: { shortLabel: "CMF distributing" },
  below_vwap: { shortLabel: "Below VWAP" },
  earnings_gap: { shortLabel: "Earnings gap unsettled" },
  weekly_bear: { shortLabel: "Weekly trend down" },
};

function failEntry(id, label, why, critical) {
  return {
    id,
    label,
    pass: false,
    critical,
    why,
    shortLabel: (critical ? CRIT_META[id] : ADV_META[id])?.shortLabel ?? label,
  };
}

function passEntry(id, label, critical) {
  return { id, label, pass: true, critical, why: "", shortLabel: "" };
}

/**
 * @param {object} r — price, sm50, sm200, bb, buyScore, threeDayRule, volDiv, cmf, vwap, weeklyTrend, range52w, volBreakout, stopPrice, earningsDaysAway, pureCrypto
 */
export function calcGate(r) {
  const ema50 = r.ema50 ?? r.sm50;
  const ema200 = r.ema200 ?? r.sm200;
  const deathCross = ema50 != null && ema200 != null && ema50 < ema200;
  const extended = r.bb && r.price > r.bb.upper;
  const stop = r.stopPrice ?? r.tradeStop ?? null;
  const belowStop =
    stop != null && r.price != null && Number.isFinite(stop) && r.price < stop;
  const bearishScore = r.buyScore != null && r.buyScore <= -4;
  const checks = [
    deathCross
      ? failEntry("death_cross", "EMA structure OK", "EMA50 < EMA200 — no longs.", true)
      : passEntry("death_cross", "EMA structure OK", true),
    extended
      ? failEntry("extended_bb", "Not extended (BB upper)", "Price above BB upper.", false)
      : passEntry("extended_bb", "Not extended (BB upper)", false),
    belowStop
      ? failEntry("below_stop", "Above stop level", "Price below trade stop.", true)
      : passEntry("below_stop", "Above stop level", true),
    bearishScore
      ? failEntry(
          "bearish_score",
          "Score not deeply bearish",
          `Score ${r.buyScore} — too bearish for long entry.`,
          true
        )
      : passEntry("bearish_score", "Score not deeply bearish", true),
    r.threeDayRule?.postDrop?.active
      ? failEntry(
          "post_drop",
          "No post-drop cooldown",
          `Drop ${r.threeDayRule.postDrop.dropPct?.toFixed(1)}% — cooldown active.`,
          true
        )
      : passEntry("post_drop", "No post-drop cooldown", true),
    r.volDiv?.bearish
      ? failEntry("vol_div", "No bearish vol divergence", "Price up on falling volume.", false)
      : passEntry("vol_div", "No bearish vol divergence", false),
    r.cmf != null && r.cmf <= -0.05
      ? failEntry("cmf_dist", "CMF not distributing", `CMF ${r.cmf.toFixed(3)}`, false)
      : passEntry("cmf_dist", "CMF not distributing", false),
    r.vwap && r.price <= r.vwap
      ? failEntry("below_vwap", "Price above VWAP", "Below VWAP.", false)
      : passEntry("below_vwap", "Price above VWAP", false),
    ...(!r.pureCrypto
      ? [
          r.threeDayRule?.earningsGap?.active
            ? failEntry(
                "earnings_gap",
                "Gap stabilized",
                `${r.threeDayRule.earningsGap.stabilLeft}d to stabilize.`,
                false
              )
            : passEntry("earnings_gap", "Gap stabilized", false),
        ]
      : []),
    r.weeklyTrend?.downtrend
      ? failEntry("weekly_bear", "Weekly trend not bearish", "Weekly downtrend.", false)
      : passEntry("weekly_bear", "Weekly trend not bearish", false),
    r.range52w?.score > 90 && r.cmf != null && r.cmf < 0.02
      ? failEntry(
          "extended_distribution",
          "Not extended with distribution",
          `52W ${r.range52w.score}/100 + weak CMF.`,
          true
        )
      : passEntry("extended_distribution", "Not extended with distribution", true),
    r.volBreakout?.volFakeout
      ? failEntry(
          "fakeout",
          "No fakeout breakout",
          `Breakout vol ${r.volBreakout.volRatio?.toFixed(1)}x.`,
          true
        )
      : passEntry("fakeout", "No fakeout breakout", true),
  ];

  const passed = checks.filter((c) => c.pass).length;
  const critFails = checks.filter((c) => c.critical && !c.pass);
  const advisoryFails = checks.filter((c) => !c.critical && !c.pass);
  const blocked = critFails.length > 0;
  const proximity = blocked
    ? "BLOCKED"
    : passed >= checks.length - 1
      ? "CLOSE"
      : passed >= checks.length - 3
        ? "NEAR"
        : "FAR";
  const bullish = r.buyScore != null && r.buyScore >= 5;
  const bearish = r.buyScore != null && r.buyScore <= -5;

  let label;
  let color;
  if (blocked) {
    label = "BLOCKED";
    color = COLORS.red;
  } else if (bullish && passed >= checks.length - 1) {
    label = "GO";
    color = COLORS.green;
  } else if (bullish && critFails.length === 0 && advisoryFails.length <= 2) {
    label = "CLOSE";
    color = COLORS.cyan;
  } else if (bullish && critFails.length === 0) {
    label = "NEAR";
    color = COLORS.yellow;
  } else if (bearish) {
    label = "AVOID";
    color = COLORS.orange;
  } else {
    label = "WAIT";
    color = COLORS.textDim;
  }

  const failedCritical = critFails.map((c) => ({
    id: c.id,
    shortLabel: c.shortLabel || c.label,
  }));
  const failedAdvisory = advisoryFails.map((c) => ({
    id: c.id,
    shortLabel: c.shortLabel || c.label,
  }));

  return {
    checks,
    passed,
    total: checks.length,
    critFails,
    blocked,
    label,
    color,
    proximity,
    allCritPass: critFails.length === 0,
    advisoryFails,
    failedCritical,
    failedAdvisory,
  };
}

/** Dev-only: log gate/score combos that violate calcGate label rules. */
export function assertGateScoreConsistency(result) {
  if (typeof process !== "undefined" && process.env?.NODE_ENV === "production") return;
  const g = result?.gate;
  const s = result?.buyScore;
  if (g == null || s == null) return;
  const label = g.label;
  const violations = [];
  if (s <= -4 && label === "WAIT" && !g.blocked) {
    violations.push(`score ${s} should be BLOCKED or AVOID, got ${label}`);
  }
  if (s <= -5 && label !== "AVOID" && label !== "BLOCKED") {
    violations.push(`bearish score ${s} expected AVOID/BLOCKED, got ${label}`);
  }
  if (s >= 5 && !g.blocked && label === "WAIT") {
    violations.push(`bullish score ${s} expected GO/CLOSE/NEAR, got WAIT`);
  }
  if (g.blocked && label !== "BLOCKED") {
    violations.push(`blocked flag set but label is ${label}`);
  }
  if (violations.length) {
    console.warn("[gate assert]", result.symbol ?? "?", violations.join("; "));
  }
}

export { calcGate as calcEntryGate };
